import { Route, Switch, Redirect, useLocation } from "react-router-dom";
import React, { useState, useEffect } from "react";

import "./App.css";
// public routes
import NotFound from "./components/NotFound";
import Audio from "./pages/audio/Audio";
import AudioPreview from "./pages/audio/AudioPreview";
import AudioSubcat from "./pages/audio/AudioSubcat";
import Book from "./pages/book/Book";
import BookSubcat from "./pages/book/BookSubcat";
import Booklet from "./pages/booklet/Booklet";
import BookletSubcat from "./pages/booklet/BookletSubcat";
import LandingPage from "./pages/landingPage/LandingPage";
import Magazine from "./pages/magazine/Magazine";
import MagazineSubcat from "./pages/magazine/MagazineSubcat";
import Minibook from "./pages/minibook/Minibook";
import MinibookSubcat from "./pages/minibook/MinibookSubcat";
import News from "./pages/news/News";
import NewsSubcat from "./pages/news/NewsSubcat";
import NewsView from "./pages/news/NewsView";
import Video from "./pages/video/Video";
import VideoSubcat from "./pages/video/VideoSubcat";
import VideoView from "./pages/video/VideoView";
import BookPreview from "./pages/book/BookPreview";
import BookletPreview from "./pages/booklet/BookletPreview";
import MagazinePreview from "./pages/magazine/MagazinePreview";
import MinibookPreview from "./pages/minibook/MinibookPreview";
import Home from "./pages/Home";

// admin routes
import Login from "./admin/Login";
import RegularLayout from "./RegularLayout";
import AdminLayout from "./AdminLayout";
import Dashboard from "./admin/Dashboard";
import Profile from "./admin/Profile";
import Analytics from "./admin/Analytics";
import Upload from "./admin/Upload";
import Logout from "./admin/Logout";
import ManageContent from "./admin/ManageContent";
import config from "./config";
import Usermanagement from "./admin/Usermanagement";

const regularRoutes = [
  { path: "/all", component: LandingPage },
  { path: "/news", component: News },
  { path: "/news/:subcategory", component: NewsSubcat },
  { path: "/news/:subcategory/:id", component: NewsView },
  { path: "/video", component: Video },
  { path: "/video/:subcategory", component: VideoSubcat },
  { path: "/video/:subcategory/:id", component: VideoView },
  { path: "/audio", component: Audio },
  { path: "/audio/:subcategory", component: AudioSubcat },
  { path: "/audio/:subcategory/:id", component: AudioPreview },
  { path: "/book", component: Book },
  { path: "/book/:subcategory", component: BookSubcat },
  { path: "/book/:subcategory/:id", component: BookPreview },
  { path: "/booklet", component: Booklet },
  { path: "/booklet/:subcategory", component: BookletSubcat },
  { path: "/booklet/:subcategory/:id", component: BookletPreview },
  { path: "/minibook", component: Minibook },
  { path: "/minibook/:subcategory", component: MinibookSubcat },
  { path: "/minibook/:subcategory/:id", component: MinibookPreview },
  { path: "/magazine", component: Magazine },
  { path: "/magazine/:subcategory", component: MagazineSubcat },
  { path: "/magazine/:subcategory/:id", component: MagazinePreview },
];

const adminRoutes = [
  { path: "/auth/dashboard", component: Dashboard },
  { path: "/auth/analytics", component: Analytics },
  { path: "/auth/upload", component: Upload },
  { path: "/auth/contents", component: ManageContent },
  { path: "/auth/profile", component: Profile },
  { path: "/auth/usermanagement", component: Usermanagement },
];

function PrivateRoute({
  isAuthenticated,
  userRole,
  allowedRoles,
  component: Component,
  ...rest
}) {
  return (
    <Route
      {...rest}
      render={(props) =>
        isAuthenticated &&
        (allowedRoles.includes(userRole) || allowedRoles.length === 0) ? (
          <Component {...props} />
        ) : (
          <Redirect
            to={{
              pathname: "/login",
              state: { from: props.location },
            }}
          />
        )
      }
    />
  );
}

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null); // Initialize as null to handle loading state properly
  const [loading, setLoading] = useState(true);

  const location = useLocation();

  useEffect(() => {
    fetch(`${config.apiUrl}adminapi.php?option=4`, {
      method: "GET",
      credentials: "include",
    })
      .then((response) => response.json())
      .then((data) => {
        setIsAuthenticated(data.authenticated);
        setUser(data.user || {}); // Ensure user is always an object
      })
      .catch((error) => console.error("Error fetching user:", error))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-grow" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <Switch>
      {/* Public routes */}
      <Route path="/" exact component={Home} />

      {/* Regular routes accessible to everyone */}
      {regularRoutes.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          exact
          render={(props) => (
            <RegularLayout>
              <route.component {...props} />
            </RegularLayout>
          )}
        />
      ))}

      {/* Admin routes with role-based access control */}
      {user && // Ensure user is defined before rendering admin routes
        adminRoutes.map((route) => (
          <PrivateRoute
            key={route.path}
            path={route.path}
            isAuthenticated={isAuthenticated}
            userRole={user.role || ""} // Default to an empty string if role is undefined
            allowedRoles={
              route.path === "/auth/analytics" ||
              route.path === "/auth/usermanagement"
                ? ["admin"]
                : route.path === "/auth/profile"
                ? ["uploader"]
                : ["admin", "uploader"] // All other routes are accessible to both roles
            }
            component={(props) => (
              <AdminLayout isAuthenticated={isAuthenticated} user={user}>
                <route.component {...props} />
              </AdminLayout>
            )}
          />
        ))}

      {/* Login route */}
      <Route path="/login">
        {isAuthenticated ? (
          <Redirect to={location.state?.from?.pathname || "/auth/dashboard"} />
        ) : (
          <Login setUser={setUser} setIsAuthenticated={setIsAuthenticated} />
        )}
      </Route>

      {/* Logout route */}
      <Route path="/logout">
        <Logout setIsAuthenticated={setIsAuthenticated} />
      </Route>

      {/* Fallback route for 404 */}
      <Route>
        <NotFound />
      </Route>
    </Switch>
  );
}
